async function loadPolyfills() {
  if (!window.IntersectionObserver) {
    await import('intersection-observer');
  }
}

function fromEntries(entries: [string, any][]) {
  const res: { [key: string]: any } = {};
  for (let i = 0; i < entries.length; i++) res[entries[i][0]] = entries[i][1];
  return res;
}

if (!Object.fromEntries) Object.fromEntries = fromEntries;

import './styles.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import ErrorBoundary from './components/ErrorBoundary';
import installSafeHistory from './helpers/installSafeHistory';
import { markAppScrollOwnershipTaken } from './helpers/scrollAnchorRestorationCoordinator';
import installDomMutationGuard from './helpers/installDomMutationGuard';
import { startPageDeathTracker } from './helpers/pageDeathTracker';
import { registerChatPushServiceWorkerIfEnabled } from './helpers/desktopNotifications';
import { BrowserRouter } from 'react-router-dom';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAlignJustify } from '@fortawesome/pro-solid-svg-icons/faAlignJustify';
import { faAward } from '@fortawesome/pro-solid-svg-icons/faAward';
import { faBullhorn } from '@fortawesome/pro-solid-svg-icons/faBullhorn';
import { faBroom } from '@fortawesome/pro-solid-svg-icons/faBroom';
import { faAndroid } from '@fortawesome/free-brands-svg-icons/faAndroid';
import { faApple } from '@fortawesome/free-brands-svg-icons/faApple';
import { faAnglesDown } from '@fortawesome/pro-solid-svg-icons/faAnglesDown';
import { faAnglesUp } from '@fortawesome/pro-solid-svg-icons/faAnglesUp';
import { faArchive } from '@fortawesome/pro-solid-svg-icons/faArchive';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faArrowDown } from '@fortawesome/pro-solid-svg-icons/faArrowDown';
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons/faArrowUp';
import { faBadgeDollar } from '@fortawesome/pro-solid-svg-icons/faBadgeDollar';
import { faBadgeDollar as farBadgeDollar } from '@fortawesome/pro-regular-svg-icons/faBadgeDollar';
import { faBadgeDollar as falBadgeDollar } from '@fortawesome/pro-light-svg-icons/faBadgeDollar';
import { faBan } from '@fortawesome/pro-solid-svg-icons/faBan';
import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars';
import { faBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { faBellSlash } from '@fortawesome/pro-solid-svg-icons/faBellSlash';
import { faBatteryFull } from '@fortawesome/pro-solid-svg-icons/faBatteryFull';
import { faBatteryEmpty } from '@fortawesome/pro-solid-svg-icons/faBatteryEmpty';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook';
import { faBookOpen } from '@fortawesome/pro-solid-svg-icons/faBookOpen';
import { faBookmark } from '@fortawesome/pro-solid-svg-icons/faBookmark';
import { faBookmark as farBookmark } from '@fortawesome/pro-regular-svg-icons/faBookmark';
import { faBrain } from '@fortawesome/pro-solid-svg-icons/faBrain';
import { faBriefcase } from '@fortawesome/pro-solid-svg-icons/faBriefcase';
import { faCameraAlt } from '@fortawesome/pro-solid-svg-icons/faCameraAlt';
import { faCameraSlash } from '@fortawesome/pro-solid-svg-icons/faCameraSlash';
import { faCardsBlank } from '@fortawesome/pro-solid-svg-icons/faCardsBlank';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCertificate } from '@fortawesome/pro-solid-svg-icons/faCertificate';
import { faCertificate as farCertificate } from '@fortawesome/pro-regular-svg-icons/faCertificate';
import { faCircle as farCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { faChalkboard } from '@fortawesome/pro-solid-svg-icons/faChalkboard';
import { faChalkboardTeacher } from '@fortawesome/pro-solid-svg-icons/faChalkboardTeacher';
import { faChartLine } from '@fortawesome/pro-solid-svg-icons/faChartLine';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faChess } from '@fortawesome/pro-solid-svg-icons/faChess';
import { faChild } from '@fortawesome/pro-solid-svg-icons/faChild';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle';
import { faClipboardCheck } from '@fortawesome/pro-solid-svg-icons/faClipboardCheck';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { faClockRotateLeft } from '@fortawesome/pro-solid-svg-icons/faClockRotateLeft';
import { faCoins } from '@fortawesome/pro-solid-svg-icons/faCoins';
import { faCloudUploadAlt } from '@fortawesome/pro-solid-svg-icons/faCloudUploadAlt';
import { faCode } from '@fortawesome/pro-solid-svg-icons/faCode';
import { faCodeBranch } from '@fortawesome/pro-solid-svg-icons/faCodeBranch';
import { faComment } from '@fortawesome/pro-solid-svg-icons/faComment';
import { faCommentAlt } from '@fortawesome/pro-solid-svg-icons/faCommentAlt';
import { faComments } from '@fortawesome/pro-solid-svg-icons/faComments';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faClone } from '@fortawesome/pro-solid-svg-icons/faClone';
import { faCrown } from '@fortawesome/pro-solid-svg-icons/faCrown';
import { faCubes } from '@fortawesome/pro-solid-svg-icons/faCubes';
import { faDharmachakra } from '@fortawesome/pro-solid-svg-icons/faDharmachakra';
import { faDesktop } from '@fortawesome/pro-solid-svg-icons/faDesktop';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import { faEdit } from '@fortawesome/pro-solid-svg-icons/faEdit';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';
import { faEraser } from '@fortawesome/pro-solid-svg-icons/faEraser';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash';
import { faExchangeAlt } from '@fortawesome/pro-solid-svg-icons/faExchangeAlt';
import { faExpand } from '@fortawesome/pro-solid-svg-icons/faExpand';
import { faCompress } from '@fortawesome/pro-solid-svg-icons/faCompress';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons/faExternalLinkAlt';
import { faEyeDropper } from '@fortawesome/pro-solid-svg-icons/faEyeDropper';
import { faFilm } from '@fortawesome/pro-solid-svg-icons/faFilm';
import { faFile } from '@fortawesome/pro-solid-svg-icons/faFile';
import { faFileArchive } from '@fortawesome/pro-solid-svg-icons/faFileArchive';
import { faFileAudio } from '@fortawesome/pro-solid-svg-icons/faFileAudio';
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons/faFilePdf';
import { faCreditCard } from '@fortawesome/pro-solid-svg-icons/faCreditCard';
import { faFileText } from '@fortawesome/pro-solid-svg-icons/faFileText';
import { faFileCsv } from '@fortawesome/pro-solid-svg-icons/faFileCsv';
import { faFileVideo } from '@fortawesome/pro-solid-svg-icons/faFileVideo';
import { faFileWord } from '@fortawesome/pro-solid-svg-icons/faFileWord';
import { faFileImage } from '@fortawesome/pro-solid-svg-icons/faFileImage';
import { faFillDrip } from '@fortawesome/pro-solid-svg-icons/faFillDrip';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';
import { faFire } from '@fortawesome/pro-solid-svg-icons/faFire';
import { faFolderOpen } from '@fortawesome/pro-solid-svg-icons/faFolderOpen';
import { faFont } from '@fortawesome/pro-solid-svg-icons/faFont';
import { faGithub } from '@fortawesome/free-brands-svg-icons/faGithub';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons/faGlobe';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons/faGripDotsVertical';
import { faGripLines } from '@fortawesome/pro-solid-svg-icons/faGripLines';
import { faHammer } from '@fortawesome/pro-solid-svg-icons/faHammer';
import { faLayerGroup } from '@fortawesome/pro-solid-svg-icons/faLayerGroup';
import { faLevelUp } from '@fortawesome/pro-solid-svg-icons/faLevelUp';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHandHolding } from '@fortawesome/pro-solid-svg-icons/faHandHolding';
import { faHeartSquare } from '@fortawesome/pro-solid-svg-icons/faHeartSquare';
import { faHistory } from '@fortawesome/pro-solid-svg-icons/faHistory';
import { faHome } from '@fortawesome/pro-solid-svg-icons/faHome';
import { faHtml5 } from '@fortawesome/free-brands-svg-icons/faHtml5';
import { faIndent } from '@fortawesome/pro-solid-svg-icons/faIndent';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faImage } from '@fortawesome/pro-solid-svg-icons/faImage';
import { faImages } from '@fortawesome/pro-solid-svg-icons/faImages';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { faGift } from '@fortawesome/pro-solid-svg-icons/faGift';
import { faJs } from '@fortawesome/free-brands-svg-icons/faJs';
import { faLeftToLine } from '@fortawesome/pro-solid-svg-icons/faLeftToLine';
import { faLightbulb } from '@fortawesome/pro-solid-svg-icons/faLightbulb';
import { faLaptopCode } from '@fortawesome/pro-solid-svg-icons/faLaptopCode';
import { faList } from '@fortawesome/pro-solid-svg-icons/faList';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faMagic } from '@fortawesome/pro-solid-svg-icons/faMagic';
import { faMicrophone } from '@fortawesome/pro-solid-svg-icons/faMicrophone';
import { faMicrophoneSlash } from '@fortawesome/pro-solid-svg-icons/faMicrophoneSlash';
import { faMoneyBillTrendUp } from '@fortawesome/pro-solid-svg-icons/faMoneyBillTrendUp';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';
import { faMinus } from '@fortawesome/pro-solid-svg-icons/faMinus';
import { faMobileAlt } from '@fortawesome/pro-solid-svg-icons/faMobileAlt';
import { faPalette } from '@fortawesome/pro-solid-svg-icons/faPalette';
import { faPaperclip } from '@fortawesome/pro-solid-svg-icons/faPaperclip';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane';
import { faPartyHorn } from '@fortawesome/pro-solid-svg-icons/faPartyHorn';
import { faPencilAlt } from '@fortawesome/pro-solid-svg-icons/faPencilAlt';
import { faPhoneSlash } from '@fortawesome/pro-solid-svg-icons/faPhoneSlash';
import { faPhoneVolume } from '@fortawesome/pro-solid-svg-icons/faPhoneVolume';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faPause } from '@fortawesome/pro-solid-svg-icons/faPause';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faPortalEnter } from '@fortawesome/pro-solid-svg-icons/faPortalEnter';
import { faPuzzlePiece } from '@fortawesome/pro-solid-svg-icons/faPuzzlePiece';
import { faQuestion } from '@fortawesome/pro-solid-svg-icons/faQuestion';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';
import { faRankingStar } from '@fortawesome/pro-solid-svg-icons/faRankingStar';
import { faReact } from '@fortawesome/free-brands-svg-icons/faReact';
import { faRecycle } from '@fortawesome/pro-solid-svg-icons/faRecycle';
import { faRedo } from '@fortawesome/pro-solid-svg-icons/faRedo';
import { faRepeat } from '@fortawesome/pro-solid-svg-icons/faRepeat';
import { faReply } from '@fortawesome/pro-solid-svg-icons/faReply';
import { faRightFromBracket } from '@fortawesome/pro-solid-svg-icons/faRightFromBracket';
import { faRobot } from '@fortawesome/pro-solid-svg-icons/faRobot';
import { faRocket } from '@fortawesome/pro-solid-svg-icons/faRocket';
import { faRocketLaunch } from '@fortawesome/pro-solid-svg-icons/faRocketLaunch';
import { faSave } from '@fortawesome/pro-solid-svg-icons/faSave';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { faSchool } from '@fortawesome/pro-solid-svg-icons/faSchool';
import { faShoppingCart } from '@fortawesome/pro-solid-svg-icons/faShoppingCart';
import { faShoppingBag } from '@fortawesome/pro-solid-svg-icons/faShoppingBag';
import { faShare } from '@fortawesome/pro-solid-svg-icons/faShare';
import { faSignOutAlt } from '@fortawesome/pro-solid-svg-icons/faSignOutAlt';
import { faSlidersH } from '@fortawesome/pro-solid-svg-icons/faSlidersH';
import { faSort } from '@fortawesome/pro-solid-svg-icons/faSort';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faSpellCheck } from '@fortawesome/pro-solid-svg-icons/faSpellCheck';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faStarHalfAlt } from '@fortawesome/pro-solid-svg-icons/faStarHalfAlt';
import { faStar as farStar } from '@fortawesome/pro-regular-svg-icons/faStar';
import { faStarHalfAlt as farStarHalfAlt } from '@fortawesome/pro-regular-svg-icons/faStarHalfAlt';
import { faStop } from '@fortawesome/pro-solid-svg-icons/faStop';
import { faSurprise } from '@fortawesome/pro-solid-svg-icons/faSurprise';
import { faSync } from '@fortawesome/pro-solid-svg-icons/faSync';
import { faTag } from '@fortawesome/pro-solid-svg-icons/faTag';
import { faTasks } from '@fortawesome/pro-solid-svg-icons/faTasks';
import { faThumbtack } from '@fortawesome/pro-solid-svg-icons/faThumbtack';
import { faThumbtack as farThumbtack } from '@fortawesome/pro-regular-svg-icons/faThumbtack';
import { faThumbsDown } from '@fortawesome/pro-solid-svg-icons/faThumbsDown';
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons/faThumbsUp';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle';
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons/faTrashAlt';
import { faTrashRestore } from '@fortawesome/pro-solid-svg-icons/faTrashRestore';
import { faTree } from '@fortawesome/pro-solid-svg-icons/faTree';
import { faTrophy } from '@fortawesome/pro-solid-svg-icons/faTrophy';
import { faUndo } from '@fortawesome/pro-solid-svg-icons/faUndo';
import { faUnlock } from '@fortawesome/pro-solid-svg-icons/faUnlock';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { faUserPlus } from '@fortawesome/pro-solid-svg-icons/faUserPlus';
import { faCalendarDay } from '@fortawesome/pro-solid-svg-icons/faCalendarDay';
import { faUserCheck } from '@fortawesome/pro-solid-svg-icons/faUserCheck';
import { faQrcode } from '@fortawesome/pro-solid-svg-icons/faQrcode';
import { faVolumeHigh } from '@fortawesome/pro-solid-svg-icons/faVolumeHigh';
import { faWifi } from '@fortawesome/pro-solid-svg-icons/faWifi';
import { faEarListen } from '@fortawesome/pro-solid-svg-icons/faEarListen';
import { faMobileScreenButton } from '@fortawesome/pro-solid-svg-icons/faMobileScreenButton';
import { faTowerBroadcast } from '@fortawesome/pro-solid-svg-icons/faTowerBroadcast';
import { faKey } from '@fortawesome/pro-solid-svg-icons/faKey';
import { faArrowRotateRight } from '@fortawesome/pro-solid-svg-icons/faArrowRotateRight';
import { faHandshake } from '@fortawesome/pro-solid-svg-icons/faHandshake';
import { faCamera } from '@fortawesome/pro-solid-svg-icons/faCamera';
import { faArrowsAlt } from '@fortawesome/pro-solid-svg-icons/faArrowsAlt';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons/faLocationDot';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faUserShield } from '@fortawesome/pro-solid-svg-icons/faUserShield';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons/faMapMarkerAlt';
import { faHourglassHalf } from '@fortawesome/pro-solid-svg-icons/faHourglassHalf';
import { faAddressBook } from '@fortawesome/pro-solid-svg-icons/faAddressBook';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup';
import { faUserEdit } from '@fortawesome/pro-solid-svg-icons/faUserEdit';
import { faUpload } from '@fortawesome/pro-solid-svg-icons/faUpload';
import { faUserGraduate } from '@fortawesome/pro-solid-svg-icons/faUserGraduate';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { faUserGroupCrown } from '@fortawesome/pro-solid-svg-icons/faUserGroupCrown';
import { faVolume } from '@fortawesome/pro-solid-svg-icons/faVolume';
import { faVolumeMute } from '@fortawesome/pro-solid-svg-icons/faVolumeMute';
import { faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons/faWandMagicSparkles';
import { faWrench } from '@fortawesome/pro-solid-svg-icons/faWrench';
import { faWindows } from '@fortawesome/free-brands-svg-icons/faWindows';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { AppContextProvider } from './contexts';
import { ToastProvider } from './contexts/Toast';
import { RootThemeProvider } from './theme/RootThemeProvider';
import App from './containers/App';
import { NavigationFeedbackProvider } from './containers/App/navigationFeedback';
import { applyThemeVars, type ThemeName } from './theme';
import { DEFAULT_PROFILE_THEME } from './constants/defaultValues';
import { getStoredItem } from './helpers/userDataHelpers';
import { install } from 'resize-observer';

if (!window.ResizeObserver) install();

declare global {
  interface Document {
    msHidden?: boolean;
    webkitHidden?: boolean;
    msvisibilitychange?: string;
    webkitvisibilitychange?: string;
  }
  interface Window {
    loadImage: (
      imageUri: string,
      callback: (img: any) => void,
      options?: any
    ) => void;
    gtag: (command: string, ...args: any[]) => void;
    twinkleAnalyticsEnabled: boolean;
    twinkleAnalyticsIdentityReady: boolean;
    twinkleConfigureAnalytics: () => void;
    twinkleAnalyticsConfigured?: boolean;
    twinkleOnAnalyticsConfigured?: () => void;
    twinkleAnalyticsUrl?: (input: string) => string;
  }
}

library.add(
  faAlignJustify,
  faAndroid,
  faAnglesDown,
  faAnglesUp,
  faApple,
  faArchive,
  faArrowLeft,
  faArrowRight,
  faArrowDown,
  faArrowUp,
  faAward,
  faBullhorn,
  faBadgeDollar,
  farBadgeDollar,
  falBadgeDollar,
  faBan,
  faBars,
  faBell,
  faBellSlash,
  faBatteryFull,
  faBatteryEmpty,
  faBolt,
  faBroom,
  faBook,
  faBookOpen,
  faBookmark,
  farBookmark,
  farCircle,
  faBrain,
  faBriefcase,
  faCode,
  faCodeBranch,
  faCameraAlt,
  faCameraSlash,
  faCardsBlank,
  faCaretDown,
  faCertificate,
  farCertificate,
  faChalkboard,
  faChalkboardTeacher,
  faChartLine,
  faCheck,
  faCheckCircle,
  faChess,
  faChild,
  faChevronUp,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faClipboardCheck,
  faClock,
  faClockRotateLeft,
  faCoins,
  faCloudUploadAlt,
  faComment,
  faCommentAlt,
  faComments,
  faClone,
  faCopy,
  faCrown,
  faCubes,
  faEye,
  faDharmachakra,
  faDesktop,
  faDownload,
  faEdit,
  faEllipsisH,
  faEraser,
  faExchangeAlt,
  faExternalLinkAlt,
  faExclamationCircle,
  faExclamationTriangle,
  faEyeDropper,
  faEyeSlash,
  faFile,
  faFileArchive,
  faFileAudio,
  faFilePdf,
  faCreditCard,
  faFileCsv,
  faFileText,
  faFileVideo,
  faFileWord,
  faFileImage,
  faFillDrip,
  faFilm,
  faExpand,
  faCompress,
  faFire,
  faFlag,
  faFolderOpen,
  faFont,
  faGithub,
  faGlobe,
  faGripDotsVertical,
  faGripLines,
  faHammer,
  faHandHolding,
  faHeart,
  faHeartSquare,
  faHistory,
  faHome,
  faHtml5,
  faImage,
  faImages,
  faGear,
  faGift,
  faIndent,
  faInfoCircle,
  faJs,
  faLevelUp,
  faLayerGroup,
  faLaptopCode,
  faLeftToLine,
  faLightbulb,
  faLink,
  faList,
  faLock,
  faMagic,
  faMagnifyingGlass,
  faMicrophone,
  faMicrophoneSlash,
  faMinus,
  faMobileAlt,
  faMoneyBillTrendUp,
  faPalette,
  faPartyHorn,
  faPaperclip,
  faPaperPlane,
  faPencilAlt,
  faPhoneSlash,
  faPhoneVolume,
  faPlay,
  faPause,
  faPlus,
  faPortalEnter,
  faPuzzlePiece,
  faQuestionCircle,
  faRankingStar,
  faReact,
  faRecycle,
  faRedo,
  faRepeat,
  faReply,
  faRightFromBracket,
  faRobot,
  faRocket,
  faRocketLaunch,
  faQuestion,
  faSchool,
  faShoppingCart,
  faShoppingBag,
  faSave,
  faSearch,
  faShare,
  faSignOutAlt,
  faSlidersH,
  faSort,
  faSparkles,
  faSpinner,
  faSpellCheck,
  faStar,
  faStarHalfAlt,
  faStop,
  farStar,
  farStarHalfAlt,
  faSurprise,
  faSync,
  faTag,
  faTasks,
  faThumbsDown,
  faThumbsUp,
  faThumbtack,
  farThumbtack,
  faTimes,
  faTimesCircle,
  faTrashAlt,
  faTrashRestore,
  faTree,
  faTrophy,
  faUpload,
  faUndo,
  faUnlock,
  faUser,
  faUserPlus,
  faCalendarDay,
  faUserCheck,
  faQrcode,
  faVolumeHigh,
  faWifi,
  faEarListen,
  faMobileScreenButton,
  faTowerBroadcast,
  faKey,
  faArrowRotateRight,
  faHandshake,
  faCamera,
  faArrowsAlt,
  faLocationDot,
  faCircleCheck,
  faUserShield,
  faEnvelope,
  faMapMarkerAlt,
  faHourglassHalf,
  faAddressBook,
  faUserGroup,
  faUserEdit,
  faUserGraduate,
  faUserGroupCrown,
  faUsers,
  faVolume,
  faVolumeMute,
  faWandMagicSparkles,
  faWrench,
  faWindows,
  faXmark,
  faYoutube
);

(async () => {
  try {
    const path = window.location?.pathname || '';
    const routeTheme = path.startsWith('/users/')
      ? (getStoredItem('routeProfileTheme') as ThemeName | null)
      : null;
    const storedTheme = (getStoredItem('profileTheme') ||
      DEFAULT_PROFILE_THEME) as ThemeName;
    applyThemeVars((routeTheme || storedTheme) as ThemeName);
  } catch (_err) {
    // ignore
  }
  await loadPolyfills();
  installSafeHistory();
  // The app runs its own scroll restoration (useScrollAnchorRestoration), but
  // its anchor cache is in-memory, so it can only own scroll for
  // same-document navigation. Ownership hands over at the FIRST in-app
  // pushState: before it, a fresh document's back/forward targets are all
  // cross-document, so native restoration must stay on 'auto' to handle
  // reload and bfcache-evicted returns. Setting 'manual' at the first push
  // marks the current entry and every entry created after it — exactly the
  // same-document traversals where the browser's deferred restore (iOS: the
  // document is the scroller via #App.ios) races the app's restore. The
  // wrapper is a transparent one-shot pre-hook: errors from the original
  // pushState propagate untouched (see installSafeHistory's note on why
  // pushState must not be error-wrapped).
  //
  // replace-based navigations deliberately do NOT take ownership: every
  // path-changing replaceState in the app today is either a load-time
  // redirect (fires before the user can scroll), a Chat/Build-editor flow
  // that does not use document scroll restoration, or same-path state
  // bookkeeping — and same-path replaces fire during initial load, where
  // flipping to 'manual' would cancel the browser's deferred reload restore.
  // If a user-triggered, path-changing replace navigation is ever added to a
  // scroll-restored surface, take ownership at that call site (or extend
  // this wrapper to replaceState gated on a pathname+search change).
  try {
    if ('scrollRestoration' in window.history) {
      const originalPushState = window.history.pushState.bind(window.history);
      window.history.pushState = function takeScrollOwnershipThenPush(
        ...args: Parameters<History['pushState']>
      ) {
        window.history.pushState = originalPushState;
        try {
          window.history.scrollRestoration = 'manual';
          markAppScrollOwnershipTaken();
        } catch (_err) {
          // sandboxed embeds can block history access
        }
        return originalPushState(...args);
      };
    }
  } catch (_err) {
    // sandboxed embeds can block history access
  }
  installDomMutationGuard();
  startPageDeathTracker();
  registerChatPushServiceWorkerIfEnabled();

  const rootElement = document.getElementById('react-view');

  if (rootElement) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <BrowserRouter>
        <ErrorBoundary componentPath="AppContext">
          <AppContextProvider>
            <RootThemeProvider>
              <ToastProvider>
                <NavigationFeedbackProvider>
                  <App />
                </NavigationFeedbackProvider>
              </ToastProvider>
            </RootThemeProvider>
          </AppContextProvider>
        </ErrorBoundary>
      </BrowserRouter>
    );
  } else {
    console.error('Could not find the root element with the ID "react-view".');
  }
})();
